import React, { useEffect, useState, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Alert,
  useTheme,
  LinearProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  QrCode as QrCodeIcon,
  Inventory2 as ContainerIcon,
} from "@mui/icons-material";
import { useReactToPrint } from "react-to-print";
import { ContainerQRPrint } from "../../components/ContainerQRPrint";
import ContainerMapView from "../../components/containers/ContainerMapView";
import { ContainerFormDialog, ContainerFormState } from "../../components/containers/ContainerFormDialog";
import { ContainerDeleteDialog } from "../../components/containers/ContainerDeleteDialog";
import { useContainerStore } from "../../stores/container.store";
import { useOrganizationStore } from "../../stores/organization.store";
import { useMaterialTypeStore } from "../../stores/recycling.store";
import {
  ContainerStatus,
  ContainerSchema,
  ContainerRequest,
} from "../../types/container.type";
import { useCollectionSiteStore } from "../../stores/collection-site.store";
import { CONTAINER_STATUS_COLORS } from "../../constants/status-colors";
import PaginationControl from "../../components/PaginationControl";
import LoadingSkeleton from "../../components/LoadingSkeleton";
import PageHeader from "../../components/PageHeader";
import { SetupCoachmark } from '../../components/selfSetup/SetupCoachmark';
import { useSelfSetupTour } from '../../hooks/useSelfSetupTour';

const ContainerList: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    containers,
    total,
    isLoading,
    error,
    getContainers,
    createContainer,
    updateContainer,
    deleteContainer,
  } = useContainerStore();
  const { selectedOrganization } = useOrganizationStore();
  const { materialTypes, fetchMaterialTypes } = useMaterialTypeStore();
  const {
    sites: collectionSites,
    getCollectionSites,
    isLoading: collectionSitesLoading,
  } = useCollectionSiteStore();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<ContainerStatus | "">("");
  const [showModal, setShowModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrContainer, setQrContainer] = useState<ContainerSchema | null>(null);
  const printRef = useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: qrContainer
      ? `RECYCLEE_${qrContainer.id}`
      : "RECYCLEE_CONTAINER_QR",
  });

  const [selectedContainer, setSelectedContainer] =
    useState<ContainerSchema | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ContainerSchema | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [dismissAddHint, setDismissAddHint] = useState(false);
  /** Knowledge Base "Go to Add container" guided tips. */
  type HelpAddPhase = 'addButton' | 'form' | 'submit';
  const [helpAddPhase, setHelpAddPhase] = useState<HelpAddPhase | null>(null);
  /** Knowledge Base "Go to Edit or delete container" row-action tips. */
  type HelpRowActionsPhase = 'edit' | 'delete';
  const [helpRowActionsPhase, setHelpRowActionsPhase] =
    useState<HelpRowActionsPhase | null>(null);
  const [helpQrHint, setHelpQrHint] = useState(false);
  const {
    isStep,
    tourPhase,
    completeStep,
    dismissTour,
    setTourPhase,
    advanceTourPhase,
  } = useSelfSetupTour();
  const tourFiltersHint = isStep('containers') && tourPhase === 'filters';
  const tourMapHint = isStep('containers') && tourPhase === 'map';
  const tourInfoHint = isStep('containers') && tourPhase === 'table';
  const tourAddHint = isStep('containers') && tourPhase === 'addButton';
  const tourFormHint =
    (isStep('containers') && tourPhase === 'form') || helpAddPhase === 'form';
  const tourSubmitHint =
    (isStep('containers') && tourPhase === 'submit') || helpAddPhase === 'submit';
  const tourDialogOpen = tourFormHint || tourSubmitHint;
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState<ContainerFormState>({
    device_id: "",
    material_type_id: "",
    fill_level: undefined as number | undefined,
    status: ContainerStatus.PENDING,
    longitude: undefined as number | undefined,
    latitude: undefined as number | undefined,
    buffer: undefined as number | undefined,
    collection_site_id: "",
    data: {
      name: "",
      description: "",
      capacity: undefined,
      capacity_unit: "kg",
      location_name: "",
      installation_date: "",
      manufacturer: "",
      model: "",
      serial_number: "",
      warranty_expiry: "",
      notes: "",
      contact_person: "",
      contact_phone: "",
      contact_email: "",
      emergency_contact: "",
      access_instructions: "",
      special_handling: "",
      environmental_notes: "",
      maintenance_notes: "",
      sensor_calibration_date: "",
      sensor_accuracy: 95,
      fill_level_threshold: 80,
      temperature_threshold: 25,
      collection_frequency: "Daily",
      collection_route: "",
      assigned_truck: "",
      assigned_driver: "",
      priority_level: "Medium",
      is_accessible: true,
      requires_special_equipment: false,
      hazardous_materials: false,
      recycling_instructions: "",
      waste_segregation_required: false,
      contamination_threshold: undefined,
      cost_per_collection: undefined,
      environmental_impact_score: undefined,
      materials: [] as string[],
      sustainability_metrics: {
        co2_reduction: 0,
        energy_saved: 0,
        water_saved: 0,
        trees_equivalent: 0,
      },
      performance_metrics: {
        collection_efficiency: 0,
        fill_rate_accuracy: 0,
        maintenance_compliance: 0,
        customer_satisfaction: 0,
      },
    },
  });

  const normalizeCapacityToKg = (capacity: number, unit: string) => {
    switch (unit) {
      case "tons":
        return capacity * 1000;
      case "kg":
        return capacity;
      case "liters":
        // depends on material density 
        return null;
      default:
        return null;
    }
  };

  const calculateFillRate = (
    fillLevelKg?: number,
    capacity?: number,
    capacityUnit?: string
  ): number | null => {
    if (fillLevelKg == null || capacity == null || !capacityUnit) return null;

    const capacityKg = normalizeCapacityToKg(capacity, capacityUnit);
    if (!capacityKg || capacityKg === 0) return null;

    return Math.min(Math.round((fillLevelKg / capacityKg) * 100), 100);
  };

  const cleanPayload = (obj: Record<string, any>) =>
    Object.fromEntries(
      Object.entries(obj).filter(([_, v]) => v !== undefined && v !== "")
    );

  const buildCreatePayload = (): ContainerRequest => ({
    device_id: formData.device_id,
    material_type_id: formData.material_type_id,
    organization_id: selectedOrganization!.id,
    status: formData.status,
    longitude: formData.longitude,
    latitude: formData.latitude,
    buffer: formData.buffer,
    fill_level: formData.fill_level,
    collection_site_id: formData.collection_site_id || undefined,
    data: formData.data,
  });

  const buildUpdatePayload = (): Partial<ContainerRequest> => {
    return cleanPayload({
      device_id: formData.device_id,
      material_type_id: formData.material_type_id,
      status: formData.status,
      longitude: formData.longitude,
      latitude: formData.latitude,
      buffer: formData.buffer,
      fill_level: formData.fill_level,
      collection_site_id: formData.collection_site_id || undefined,
      data: formData.data,
    }) as Partial<ContainerRequest>;
  };

  useEffect(() => {
    if (selectedOrganization) {
      getCollectionSites({
        organization_id: selectedOrganization.id,
        page: 1,
        limit: 100,
      });
    }
  }, [selectedOrganization]);

  useEffect(() => {
    if (selectedOrganization) {
      loadContainers();
      loadMaterialTypes();
    }
  }, [selectedOrganization, page, limit, status]);

  const loadMaterialTypes = async () => {
    if (selectedOrganization) {
      await fetchMaterialTypes(selectedOrganization.id);
    }
  };

  const loadContainers = async () => {
    if (selectedOrganization) {
      await getContainers({
        page,
        limit,
        organization_id: selectedOrganization.id,
        search: search || undefined,
        status: status || undefined,
      });
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.material_type_id) {
      errors.material_type_id = t('containers.form.errors.materialTypeRequired');
    }

    if (!formData.collection_site_id) {
      errors.collection_site_id = t('containers.form.errors.collectionSiteRequired');
    }

    if (formData.latitude == null || Number.isNaN(formData.latitude)) {
      errors.latitude = t('containers.form.errors.latitudeRequired');
    }

    if (formData.longitude == null || Number.isNaN(formData.longitude)) {
      errors.longitude = t('containers.form.errors.longitudeRequired');
    }

    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateContainer = async () => {
    if (!selectedOrganization || !validateForm()) return;

    const payload = buildCreatePayload();
    console.log("CREATE CONTAINER PAYLOAD:", payload);

    await createContainer(payload);
    setShowModal(false);
    resetForm();
  };

  const handleUpdateContainer = async () => {
    if (!selectedContainer || !validateForm()) return;

    await updateContainer(selectedContainer.id, buildUpdatePayload());

    setShowModal(false);
    setSelectedContainer(null);
  };

  const resetForm = () => {
    setFormData({
      device_id: "",
      material_type_id: "",
      status: ContainerStatus.PENDING,
      longitude: undefined,
      latitude: undefined,
      buffer: undefined,
      fill_level: undefined,
      collection_site_id: "",
      data: {
        name: "",
        description: "",
        capacity: undefined,
        capacity_unit: "kg",
        location_name: "",
        installation_date: "",
        manufacturer: "",
        model: "",
        serial_number: "",
        warranty_expiry: "",
        notes: "",
        contact_person: "",
        contact_phone: "",
        contact_email: "",
        emergency_contact: "",
        access_instructions: "",
        special_handling: "",
        environmental_notes: "",
        maintenance_notes: "",
        sensor_calibration_date: "",
        sensor_accuracy: 95,
        fill_level_threshold: 80,
        temperature_threshold: 25,
        collection_frequency: "Daily",
        collection_route: "",
        assigned_truck: "",
        assigned_driver: "",
        priority_level: "Medium",
        is_accessible: true,
        requires_special_equipment: false,
        hazardous_materials: false,
        recycling_instructions: "",
        waste_segregation_required: false,
        contamination_threshold: undefined,
        cost_per_collection: undefined,
        environmental_impact_score: undefined,
        materials: [],
        sustainability_metrics: {
          co2_reduction: 0,
          energy_saved: 0,
          water_saved: 0,
          trees_equivalent: 0,
        },
        performance_metrics: {
          collection_efficiency: 0,
          fill_rate_accuracy: 0,
          maintenance_compliance: 0,
          customer_satisfaction: 0,
        },
      },
    });

    setErrors({});
  };

  useEffect(() => {
    const state = location.state as {
      openAdd?: boolean;
      showAddHint?: boolean;
      showRowActionsHint?: boolean;
      showQrHint?: boolean;
    } | null;
    if (
      !state?.openAdd &&
      !state?.showAddHint &&
      !state?.showRowActionsHint &&
      !state?.showQrHint
    ) {
      return;
    }
    if (state.openAdd) {
      setSelectedContainer(null);
      resetForm();
      setShowModal(true);
    }
    if (state.showAddHint) {
      setHelpAddPhase('addButton');
      setDismissAddHint(false);
    }
    if (state.showRowActionsHint) {
      setHelpRowActionsPhase('edit');
    }
    if (state.showQrHint) {
      setHelpQrHint(true);
    }
    if (state.showRowActionsHint || state.showQrHint) {
      setSearch('');
      setStatus('');
    }
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);

  const finishHelpAddGuide = () => {
    setHelpAddPhase(null);
    setShowModal(false);
    setSelectedContainer(null);
  };

  const handleDeleteContainer = (containerId: string) => {
    const container = containers.find((item) => item.id === containerId);
    if (container) {
      setDeleteTarget(container);
    }
  };

  const handleConfirmDeleteContainer = async () => {
    if (!deleteTarget || deleteSubmitting) return;

    setDeleteSubmitting(true);
    try {
      const success = await deleteContainer(deleteTarget.id);
      if (success) {
        setDeleteTarget(null);
      }
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const deleteTargetSiteName = useMemo(() => {
    if (!deleteTarget?.collection_site_id) return undefined;
    return collectionSites.find((site) => site.id === deleteTarget.collection_site_id)?.data?.name;
  }, [deleteTarget, collectionSites]);

  const handleGenerateQR = (container: ContainerSchema) => {
    setQrContainer(container);
    setShowQRModal(true);
  };

  const handleEditContainer = (container: ContainerSchema) => {
    setSelectedContainer(container);
    setFormData((prev) => ({
      device_id: container.device_id,
      material_type_id: container.material_type_id || "",
      status: container.status,
      longitude: container.longitude,
      latitude: container.latitude,
      buffer: container.buffer,
      fill_level: container.fill_level,
      collection_site_id: container.collection_site_id || "",
      data: {
        ...prev.data,
        ...container.data,
      },
    }));
    setShowModal(true);
  };

  // Self-setup: open create dialog once when entering form/submit tip phases.
  useEffect(() => {
    if (!tourDialogOpen) return;
    if (showModal) return;
    setSelectedContainer(null);
    resetForm();
    setErrors({});
    setShowModal(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when entering dialog tip phases
  }, [tourDialogOpen]);

  // Only from Help deep-link or setup dialog — not auto on empty list.
  const showAddHint =
    !dismissAddHint &&
    !isLoading &&
    !showModal &&
    (tourAddHint ||
      (helpAddPhase === 'addButton' &&
        !(
          isStep('containers') &&
          (tourPhase === 'nav' ||
            tourPhase === 'filters' ||
            tourPhase === 'map' ||
            tourPhase === 'table' ||
            tourPhase === 'form' ||
            tourPhase === 'submit')
        )));

  const filteredContainers = useMemo(() => {
    if (!containers?.length) return [];
    return containers.filter((container) => {
      if (!search) return true;
      const searchLower = search.toLowerCase();
      return (
        container.device_id?.toLowerCase().includes(searchLower) ||
        container.data?.name?.toLowerCase().includes(searchLower) ||
        container.data?.location_name?.toLowerCase().includes(searchLower) ||
        container.material_type?.name?.toLowerCase().includes(searchLower)
      );
    });
  }, [containers, search]);

  const statusOptions = useMemo(
    () => [
      { value: ContainerStatus.PENDING, labelKey: "containers.status.pending", color: CONTAINER_STATUS_COLORS[ContainerStatus.PENDING] },
      { value: ContainerStatus.EMPTY, labelKey: "containers.status.empty", color: CONTAINER_STATUS_COLORS[ContainerStatus.EMPTY] },
      { value: ContainerStatus.AWAITING_PICKUP, labelKey: "containers.status.awaitingPickup", color: CONTAINER_STATUS_COLORS[ContainerStatus.AWAITING_PICKUP] },
      { value: ContainerStatus.READY_FOR_PICKUP, labelKey: "containers.status.readyForPickup", color: CONTAINER_STATUS_COLORS[ContainerStatus.READY_FOR_PICKUP] },
      { value: ContainerStatus.MAINTENANCE, labelKey: "containers.status.maintenance", color: CONTAINER_STATUS_COLORS[ContainerStatus.MAINTENANCE] },
      { value: ContainerStatus.INACTIVE, labelKey: "containers.status.inactive", color: CONTAINER_STATUS_COLORS[ContainerStatus.INACTIVE] },
    ],
    []
  );

  const priorityOptions = useMemo(
    () => [
      { value: "Low", labelKey: "containers.priority.low", color: theme.palette.success.main },
      { value: "Medium", labelKey: "containers.priority.medium", color: theme.palette.info.main },
      { value: "High", labelKey: "containers.priority.high", color: theme.palette.warning.main },
      { value: "Urgent", labelKey: "containers.priority.urgent", color: theme.palette.error.main },
    ],
    [theme]
  );

  const frequencyOptions = useMemo(
    () => [
      { value: "Daily", labelKey: "containers.frequency.daily" },
      { value: "Weekly", labelKey: "containers.frequency.weekly" },
      { value: "Bi-weekly", labelKey: "containers.frequency.biWeekly" },
      { value: "Monthly", labelKey: "containers.frequency.monthly" },
      { value: "As needed", labelKey: "containers.frequency.asNeeded" },
      { value: "On-demand", labelKey: "containers.frequency.onDemand" },
    ],
    []
  );

  const unitOptions = useMemo(
    () =>
      ["kg", "tons", "lbs", "bags", "cartons", "bottles", "pallets", "cubic_yards", "cubic_meters", "liters", "gallons"].map(
        (value) => ({ value, labelKey: `containers.units.${value}` as const })
      ),
    []
  );

  const materialOptions = useMemo(
    () =>
      [
        { value: "Paper", labelKey: "containers.materialOptions.paper" },
        { value: "Plastic", labelKey: "materialTypes.categories.plastic" },
        { value: "Metal", labelKey: "materialTypes.categories.metal" },
        { value: "Glass", labelKey: "materialTypes.categories.glass" },
        { value: "Organic", labelKey: "materialTypes.categories.organic" },
        { value: "E-waste", labelKey: "materialTypes.categories.e_waste" },
        { value: "Mixed Waste", labelKey: "containers.materialOptions.mixedWaste" },
        { value: "Cardboard", labelKey: "materialTypes.categories.cardboard" },
        { value: "Textiles", labelKey: "materialTypes.categories.textiles" },
        { value: "Wood", labelKey: "materialTypes.categories.wood" },
        { value: "Rubber", labelKey: "materialTypes.categories.rubber" },
        { value: "Ceramics", labelKey: "containers.materialOptions.ceramics" },
        { value: "Construction Waste", labelKey: "containers.materialOptions.constructionWaste" },
        { value: "Hazardous Waste", labelKey: "materialTypes.categories.hazardous_waste" },
        { value: "Electronics", labelKey: "containers.materialOptions.electronics" },
        { value: "Batteries", labelKey: "containers.materialOptions.batteries" },
        { value: "Medical Waste", labelKey: "containers.materialOptions.medicalWaste" },
        { value: "Food Waste", labelKey: "containers.materialOptions.foodWaste" },
        { value: "Garden Waste", labelKey: "containers.materialOptions.gardenWaste" },
        { value: "Industrial Waste", labelKey: "containers.materialOptions.industrialWaste" },
      ] as const,
    []
  );

  const materialTypesList = useMemo(
    () => materialOptions.map((option) => option.value),
    [materialOptions]
  );

  const getMaterialLabel = (value: string) => {
    const match = materialOptions.find((option) => option.value === value);
    return match ? t(match.labelKey) : value;
  };

  if (isLoading || collectionSitesLoading) {
    return <LoadingSkeleton variant="cards" filters={4} cardCount={6} />;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 128px)",
        minHeight: 520,
      }}
    >
      <PageHeader
        icon={<ContainerIcon />}
        title={t('containers.title')}
        actions={
          <SetupCoachmark
            open={showAddHint}
            title={t('containers.addContainerHintTitle')}
            subtitle={t('containers.addContainerHint')}
            nextLabel={t('selfSetup.tourNext')}
            onNext={() => {
              setDismissAddHint(true);
              if (tourAddHint) {
                advanceTourPhase();
                return;
              }
              if (helpAddPhase === 'addButton') {
                setHelpAddPhase('form');
                setSelectedContainer(null);
                resetForm();
                setShowModal(true);
              }
            }}
            onDismiss={() => {
              setDismissAddHint(true);
              if (tourAddHint) dismissTour();
              else if (helpAddPhase === 'addButton') setHelpAddPhase(null);
            }}
          >
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setDismissAddHint(true);
                setSelectedContainer(null);
                resetForm();
                setShowModal(true);
                if (tourAddHint) setTourPhase('form');
                else if (helpAddPhase === 'addButton') setHelpAddPhase('form');
              }}
              sx={{ px: 3, py: 1.5 }}
            >
              {t('containers.addContainer')}
            </Button>
          </SetupCoachmark>
        }
      />

      <SetupCoachmark
        open={tourFiltersHint}
        fullWidth
        title={t('selfSetup.tour.containersFiltersTitle')}
        subtitle={t('selfSetup.tour.containersFiltersSubtitle')}
        nextLabel={t('selfSetup.tourNext')}
        onNext={() => advanceTourPhase()}
        onDismiss={() => dismissTour()}
      >
        <Card sx={{ mb: 2, flexShrink: 0 }}>
          <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label={t('common.search')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('common.status')}</InputLabel>
                  <Select
                    value={status}
                    label={t('common.status')}
                    onChange={(e) => setStatus(e.target.value as ContainerStatus)}
                  >
                    <MenuItem value="">{t('common.all')}</MenuItem>
                    {statusOptions?.map((s) => (
                      <MenuItem key={s.value} value={s.value}>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: "50%",
                              bgcolor: s.color,
                            }}
                          />
                          {t(s.labelKey)}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </SetupCoachmark>

      {error && (
        <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>
          {error}
        </Alert>
      )}

      <ContainerMapView
        containers={filteredContainers}
        onEdit={handleEditContainer}
        onDelete={handleDeleteContainer}
        onQr={handleGenerateQR}
        tourMapHint={tourMapHint}
        tourInfoHint={tourInfoHint}
        onTourMapNext={() => advanceTourPhase()}
        onTourInfoNext={() => advanceTourPhase()}
        onTourDismiss={() => dismissTour()}
        helpRowActionsPhase={helpRowActionsPhase}
        helpQrHint={helpQrHint}
        onHelpRowActionsNext={() => setHelpRowActionsPhase('delete')}
        onHelpRowActionsDismiss={() => setHelpRowActionsPhase(null)}
        onHelpQrDismiss={() => setHelpQrHint(false)}
      />


      {/* QR Code Dialog */}
      <Dialog
        open={showQRModal}
        onClose={() => {
          setShowQRModal(false);
          setQrContainer(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle textAlign="center">{t('containers.qrTitle')}</DialogTitle>

        <DialogContent>
          {qrContainer ? (
            <div ref={printRef}>
              <ContainerQRPrint container={qrContainer} />
            </div>
          ) : (
            <Typography color="error" textAlign="center">
              {t('containers.invalidContainer')}
            </Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", gap: 1 }}>
          <Button
            onClick={() => {
              setShowQRModal(false);
              setQrContainer(null);
            }}
          >
            {t('common.close')}
          </Button>

          <Button
            variant="contained"
            onClick={handlePrint}
            disabled={!qrContainer}
          >
            {t('common.print')}
          </Button>
        </DialogActions>
      </Dialog>

      <ContainerFormDialog
        open={showModal}
        onClose={() => {
          if (helpAddPhase) {
            finishHelpAddGuide();
            return;
          }
          if (tourFormHint || tourSubmitHint) dismissTour();
          setShowModal(false);
        }}
        isEdit={!!selectedContainer}
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        setErrors={setErrors}
        materialTypes={materialTypes ?? []}
        collectionSites={collectionSites}
        unitOptions={unitOptions}
        statusOptions={statusOptions}
        priorityOptions={priorityOptions}
        frequencyOptions={frequencyOptions}
        materialTypesList={materialTypesList}
        getMaterialLabel={getMaterialLabel}
        onSubmit={
          selectedContainer ? handleUpdateContainer : handleCreateContainer
        }
        tourFormHint={tourFormHint}
        tourSubmitHint={tourSubmitHint}
        onTourFormNext={() => {
          if (helpAddPhase === 'form') {
            setHelpAddPhase('submit');
            return;
          }
          advanceTourPhase();
        }}
        onTourSubmitDone={() => {
          if (helpAddPhase === 'submit') {
            finishHelpAddGuide();
            return;
          }
          completeStep('containers');
          setShowModal(false);
        }}
        onTourDismiss={() => {
          if (helpAddPhase) {
            finishHelpAddGuide();
            return;
          }
          dismissTour();
          setShowModal(false);
        }}
      />

      <ContainerDeleteDialog
        open={!!deleteTarget}
        container={deleteTarget}
        collectionSiteName={deleteTargetSiteName}
        loading={deleteSubmitting}
        onClose={() => {
          if (!deleteSubmitting) {
            setDeleteTarget(null);
          }
        }}
        onConfirm={handleConfirmDeleteContainer}
      />
    </Box>
  );
};

export default ContainerList;


